import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import PropTypes from 'prop-types';
import React, {useMemo, useRef} from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {downloadChartPng, downloadCsv, formatAxisDate} from './timespanUtils';

const SERIES = [
  {key: 'self_service', label: 'Self-Service', color: '#2196F3'},
  {key: 'sfdc', label: 'SFDC', color: '#4CAF50'},
  {key: 'manual', label: 'Manual', color: '#FF9800'},
];

const ProvisioningActivityChart = ({
  data,
  totals,
  isLoading,
  error,
  timespan,
  chartType,
  onChartTypeChange,
  stacked,
  onStackedChange,
  metrics,
  onMetricsChange,
  granularity,
}) => {
  const cardRef = useRef(null);
  const activeSeries = useMemo(
    () => SERIES.filter(s => metrics.includes(s.key)),
    [metrics]
  );

  const handleMetricToggle = key => {
    if (metrics.includes(key)) {
      if (metrics.length === 1) return;
      onMetricsChange(metrics.filter(m => m !== key));
    } else {
      onMetricsChange([...metrics, key]);
    }
  };

  const handleExport = () => {
    if (!data?.length) return;
    const rows = data.map(d => ({
      Date: d.date,
      'Self-Service': d.self_service ?? 0,
      SFDC: d.sfdc ?? 0,
      Manual: d.manual ?? 0,
    }));
    downloadCsv(rows, `provisioning-activity-${timespan}.csv`);
  };

  const handleDownloadPng = () =>
    downloadChartPng(cardRef.current, `provisioning-activity-${timespan}.png`);

  const tickFormatter = dateStr => formatAxisDate(dateStr, granularity);
  const ChartComponent = chartType === 'bar' ? BarChart : LineChart;

  return (
    <Card ref={cardRef} variant="outlined" sx={{mb: 3}}>
      <CardContent>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Typography variant="h6">Provisioning Activity</Typography>
          <Stack direction="row" gap={1}>
            <Button
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              disabled={!data?.length}
            >
              Export CSV
            </Button>
            <Button
              size="small"
              startIcon={<CameraAltOutlinedIcon />}
              onClick={handleDownloadPng}
              disabled={!data?.length}
            >
              PNG
            </Button>
          </Stack>
        </Stack>

        <Stack
          direction={{xs: 'column', sm: 'row'}}
          alignItems={{xs: 'flex-start', sm: 'center'}}
          gap={2}
          mb={2}
          flexWrap="wrap"
        >
          <ToggleButtonGroup
            value={chartType}
            exclusive
            onChange={(_, v) => v && onChartTypeChange(v)}
            size="small"
          >
            <ToggleButton value="bar">Bar</ToggleButton>
            <ToggleButton value="line">Line</ToggleButton>
          </ToggleButtonGroup>

          {chartType === 'bar' && (
            <FormControlLabel
              control={
                <Switch
                  checked={stacked}
                  onChange={e => onStackedChange(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  Stacked
                </Typography>
              }
            />
          )}

          <FormGroup row>
            {SERIES.map(s => (
              <FormControlLabel
                key={s.key}
                control={
                  <Checkbox
                    checked={metrics.includes(s.key)}
                    onChange={() => handleMetricToggle(s.key)}
                    size="small"
                    sx={{color: s.color, '&.Mui-checked': {color: s.color}}}
                  />
                }
                label={<Typography variant="body2">{s.label}</Typography>}
              />
            ))}
          </FormGroup>
        </Stack>

        {error && (
          <Alert severity="error" sx={{mb: 2}}>
            Failed to load provisioning data. Please try refreshing.
          </Alert>
        )}

        {isLoading && (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress size={32} />
          </Box>
        )}

        {!isLoading && !error && data && data.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            py={4}
            textAlign="center"
          >
            No provisioning activity for this period.
          </Typography>
        )}

        {!isLoading && !error && data && data.length > 0 && (
          <ResponsiveContainer width="100%" height={300}>
            <ChartComponent
              data={data}
              margin={{top: 8, right: 16, left: 0, bottom: 4}}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={tickFormatter}
                tick={{fontSize: 12}}
                tickLine={false}
              />
              <YAxis
                allowDecimals={false}
                tick={{fontSize: 12}}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                labelFormatter={v => formatAxisDate(v, granularity)}
                formatter={(value, name) => [value, name]}
              />
              <Legend iconType="circle" iconSize={8} />
              {activeSeries.map(s =>
                chartType === 'bar' ? (
                  <Bar
                    key={s.key}
                    dataKey={s.key}
                    name={s.label}
                    fill={s.color}
                    stackId={stacked ? 'stack' : undefined}
                    radius={stacked ? undefined : [3, 3, 0, 0]}
                  />
                ) : (
                  <Line
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    name={s.label}
                    stroke={s.color}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{r: 4}}
                  />
                )
              )}
            </ChartComponent>
          </ResponsiveContainer>
        )}

        {totals && !isLoading && !error && (
          <>
            <Divider sx={{my: 2}} />
            <Stack direction="row" gap={4} flexWrap="wrap">
              {SERIES.filter(s => metrics.includes(s.key)).map(s => (
                <Box key={s.key}>
                  <Typography variant="caption" color="text.secondary">
                    {s.label}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{color: s.color, fontWeight: 700}}
                  >
                    {totals[s.key]?.toLocaleString()}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  );
};

ProvisioningActivityChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  totals: PropTypes.object,
  isLoading: PropTypes.bool,
  error: PropTypes.object,
  timespan: PropTypes.string.isRequired,
  chartType: PropTypes.string.isRequired,
  onChartTypeChange: PropTypes.func.isRequired,
  stacked: PropTypes.bool.isRequired,
  onStackedChange: PropTypes.func.isRequired,
  metrics: PropTypes.arrayOf(PropTypes.string).isRequired,
  onMetricsChange: PropTypes.func.isRequired,
  granularity: PropTypes.string.isRequired,
};

export default ProvisioningActivityChart;
